import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const FeaturesEditor = () => {
  const [features, setFeatures] = useState([]);
  const [detailedFeatures, setDetailedFeatures] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isDetailedOpen, setIsDetailedOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [editingDetailed, setEditingDetailed] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    order: 0
  });
  const [detailedFormData, setDetailedFormData] = useState({
    category: '',
    title: '',
    tagline: '',
    features: [''],
    image_url: '',
    order: 0
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchFeatures();
    fetchDetailedFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/features`);
      setFeatures(response.data);
    } catch (error) {
      console.error('Failed to fetch features:', error);
    }
  };

  const fetchDetailedFeatures = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/detailed-features`);
      setDetailedFeatures(response.data);
    } catch (error) {
      console.error('Failed to fetch detailed features:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingFeature) {
        await axios.put(`${BACKEND_URL}/api/admin/features/${editingFeature.id}`, formData);
        toast({ title: 'Success', description: 'Feature updated successfully' });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/features`, formData);
        toast({ title: 'Success', description: 'Feature created successfully' });
      }
      setIsOpen(false);
      resetForm();
      fetchFeatures();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save feature', variant: 'destructive' });
    }
  };

  const handleDetailedSubmit = async () => {
    try {
      const cleanedFeatures = detailedFormData.features.filter(f => f.trim() !== '');
      const data = { ...detailedFormData, features: cleanedFeatures };
      
      if (editingDetailed) {
        await axios.put(`${BACKEND_URL}/api/admin/detailed-features/${editingDetailed.id}`, data);
        toast({ title: 'Success', description: 'Detailed feature updated successfully' });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/detailed-features`, data);
        toast({ title: 'Success', description: 'Detailed feature created successfully' });
      }
      setIsDetailedOpen(false);
      resetDetailedForm();
      fetchDetailedFeatures();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save detailed feature', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feature?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/features/${id}`);
      toast({ title: 'Success', description: 'Feature deleted successfully' });
      fetchFeatures();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete feature', variant: 'destructive' });
    }
  };

  const handleDeleteDetailed = async (id) => {
    if (!window.confirm('Delete this detailed feature?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/detailed-features/${id}`);
      toast({ title: 'Success', description: 'Detailed feature deleted successfully' });
      fetchDetailedFeatures();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete detailed feature', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', image_url: '', order: 0 });
    setEditingFeature(null);
  };

  const resetDetailedForm = () => {
    setDetailedFormData({ category: '', title: '', tagline: '', features: [''], image_url: '', order: 0 });
    setEditingDetailed(null);
  };

  const addFeatureItem = () => {
    setDetailedFormData({
      ...detailedFormData,
      features: [...detailedFormData.features, '']
    });
  };

  const updateFeatureItem = (index, value) => {
    const newFeatures = [...detailedFormData.features];
    newFeatures[index] = value;
    setDetailedFormData({ ...detailedFormData, features: newFeatures });
  };

  const removeFeatureItem = (index) => {
    setDetailedFormData({
      ...detailedFormData,
      features: detailedFormData.features.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="space-y-8">
      {/* Simple Features */}
      <Card className="p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Simple Features</h2>
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} className="bg-pink-500 hover:bg-pink-600">
                <Plus className="w-4 h-4 mr-2" />
                Add Feature
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingFeature ? 'Edit Feature' : 'Add Feature'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Image URL</Label>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Order</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  />
                </div>
                <Button onClick={handleSubmit} className="w-full">
                  {editingFeature ? 'Update' : 'Create'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <Card key={feature.id} className="p-4">
              <img src={feature.image_url} alt={feature.title} className="w-full h-40 object-cover rounded-lg mb-3" />
              <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
              <p className="text-sm text-gray-600 mb-3">{feature.description}</p>
              <div className="flex space-x-2">
                <Button size="sm" variant="outline" onClick={() => {
                  setEditingFeature(feature);
                  setFormData(feature);
                  setIsOpen(true);
                }}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(feature.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </Card>

      {/* Detailed Features */}
      <Card className="p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Detailed Features</h2>
          <Dialog open={isDetailedOpen} onOpenChange={setIsDetailedOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetDetailedForm} className="bg-pink-500 hover:bg-pink-600">
                <Plus className="w-4 h-4 mr-2" />
                Add Detailed Feature
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingDetailed ? 'Edit Detailed Feature' : 'Add Detailed Feature'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Category</Label>
                  <Input
                    value={detailedFormData.category}
                    onChange={(e) => setDetailedFormData({ ...detailedFormData, category: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Title</Label>
                  <Input
                    value={detailedFormData.title}
                    onChange={(e) => setDetailedFormData({ ...detailedFormData, title: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Tagline</Label>
                  <Input
                    value={detailedFormData.tagline}
                    onChange={(e) => setDetailedFormData({ ...detailedFormData, tagline: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Feature Points</Label>
                  {detailedFormData.features.map((feature, index) => (
                    <div key={index} className="flex space-x-2 mb-2">
                      <Input
                        value={feature}
                        onChange={(e) => updateFeatureItem(index, e.target.value)}
                        placeholder="Feature point"
                      />
                      <Button size="sm" variant="destructive" onClick={() => removeFeatureItem(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button size="sm" onClick={addFeatureItem} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Point
                  </Button>
                </div>
                <div>
                  <Label>Image URL</Label>
                  <Input
                    value={detailedFormData.image_url}
                    onChange={(e) => setDetailedFormData({ ...detailedFormData, image_url: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Order</Label>
                  <Input
                    type="number"
                    value={detailedFormData.order}
                    onChange={(e) => setDetailedFormData({ ...detailedFormData, order: parseInt(e.target.value) })}
                  />
                </div>
                <Button onClick={handleDetailedSubmit} className="w-full">
                  {editingDetailed ? 'Update' : 'Create'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-6">
          {detailedFeatures.map((feature) => (
            <Card key={feature.id} className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm text-pink-500 font-bold mb-2">{feature.category}</p>
                  <h3 className="font-bold text-2xl mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.tagline}</p>
                </div>
                <div className="flex space-x-2">
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditingDetailed(feature);
                    setDetailedFormData(feature);
                    setIsDetailedOpen(true);
                  }}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDeleteDetailed(feature.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <ul className="space-y-2 mb-4">
                {feature.features.map((f, i) => (
                  <li key={i} className="text-sm text-gray-700">• {f}</li>
                ))}
              </ul>
              <img src={feature.image_url} alt={feature.title} className="w-full h-48 object-cover rounded-lg" />
            </Card>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default FeaturesEditor;
